<script setup lang="ts">
import SVGRight from '@mop/shared/images/arrow/right.svg?component';
import { getCta } from '@mop/cms/utils/utils';
import type { CmsUiCta } from './uiCmsCta';

defineOptions({
  name: 'UiCmsCta',
});

const emit = defineEmits(['cta-click']);

const props = defineProps({
  data: {
    type: Object,
    required: true,
  },
  noPadding: {
    type: Boolean,
    default: false,
  },
});

const { $mopI18n } = useNuxtApp();
const data: any = props.data;
const element = data.component ? (getCta(data) ?? ({} as CmsUiCta)) : (props.data as CmsUiCta);
const elementText = element.text || $mopI18n.t(`components.cta.${element.textTranslationKey || 'discover_now'}`) || '';
const typeButtons = ['buttonPrimary', 'buttonSecondary'];

// Make sure "is" is set to not break the component
const link = element.link || { componentName: 'div' };
link.componentName = link.componentName || 'div';

const isBackLink = element.isBackLink ? 'click' : '';
function browseBack() {
  useRouter().back();
}

/** TODO: Legacy fallback, remove after content fully migrated */
const typeBigArrowRight = 'bigArrowRight';
</script>

<template>
  <div
    :class="[
      'ui-cms-cta',
      element.align ? `ui-cms-cta--${element.align || 'left'}` : '',
      {
        'ui-cms-padding': !noPadding,
        'ui-cms-cta--uppercase': element.isUppercase,
      },
    ]"
    :style="{
      '--color': element.textColor,
    }"
    :data-track-element-item="data.component ? elementText : undefined"
  >
    <Ui2Button
      v-if="typeButtons.includes(element.type || '')"
      v-bind="element.link"
      :appearance="element.invertedButton ? 'inverted' : 'default'"
      :label="elementText"
      :type="element.type === 'buttonSecondary' ? 'outline' : undefined"
      :size="element.size"
      :icon-end="element.withArrow ? 'arrow-right' : undefined"
      :aria-label="element.ariaLabel || undefined"
    />

    <Ui2NuxtLink
      v-else
      v-storyblok-editable="element"
      v-bind="element.link"
      :class="[
        'ui-cms-cta__link',
        `ui-cms-cta__link--size-${element.size || 'md'}`,
        {
          'ui-cms-cta__link--big-arrow': element.type === typeBigArrowRight,
        },
      ]"
      :aria-label="element.ariaLabel || undefined"
      @[isBackLink]="browseBack"
      @click="emit('cta-click')"
    >
      <span class="ui-cms-cta__text">{{ elementText }}</span>
      <SVGRight class="ui-cms-cta__arrow" />
    </Ui2NuxtLink>
  </div>
</template>

<style lang="scss" scoped>
$verticalPadding: $space15;

.ui-cms-cta {
  display: flex;
  width: 100%;
}

.ui-cms-padding {
  padding: 0 $ui-global-padding;
}

.ui-cms-cta--center {
  justify-content: center;
}

.ui-cms-cta--right {
  justify-content: flex-end;
}

.ui-cms-cta__arrow {
  fill: currentColor;
  margin-left: $space-4;
}

.ui-cms-cta__link {
  @include v2-link-style(underline);
  display: inline-flex;
  align-items: center;
  color: var(--color);

  &:hover {
    &::before {
      opacity: 0;
    }
    .ui-cms-cta__arrow {
      transform: translateX($space-8);
    }
  }
}

.ui-cms-cta__link--size-lg {
  .ui-cms-cta__text {
    @include v2-text-style(lg);
  }
  &::after {
    width: calc(100% - $space-24);
  }

  .ui-cms-cta__arrow {
    margin-left: $space-8;
    width: 20px;
    height: 20px;
  }
}
.ui-cms-cta__link--size-md {
  .ui-cms-cta__text {
    @include v2-text-style(sm);
  }
  &::after {
    width: calc(100% - 20px);
  }

  .ui-cms-cta__arrow {
    width: 16px;
    height: 16px;
  }
}
.ui-cms-cta__link--size-sm {
  .ui-cms-cta__text {
    @include v2-text-style(xs);
  }
  &::after {
    width: calc(100% - $space-16);
  }

  .ui-cms-cta__arrow {
    width: 13px;
    height: 13px;
  }
}

.ui-cms-cta--uppercase {
  .ui-cms-cta__text {
    text-transform: uppercase;
  }
}

/** TODO: Legacy fallback, remove after content fully migrated */
.ui-cms-cta__link--big-arrow {
  .ui-cms-cta__text {
    @include v2-text-style('4xl', bold);
    padding-right: 5px;
  }
  &::after {
    width: calc(100% - $space-48);
  }

  .ui-cms-cta__arrow {
    width: 35px;
    height: 35px;
  }
}
</style>
